import type { AffiliateProgramHub } from "@/services/program.service";
import { DashboardCard } from "@/components/dashboard/dashboard-card";
import { formatNumber } from "@/utils/formatters";
import { money, pct, relativeTime } from "./helpers";

/**
 * The affiliate's subscriptions with this brand: what renewals have earned
 * so far as the one figure, and the deal and standing as sentences. Shown
 * only when the brand has connected Recharge and either pays on renewals or
 * has subscriptions attributed to this affiliate.
 */
export function RecurringCard({ hub }: { hub: AffiliateProgramHub }) {
  const r = hub.recurring;
  if (!r) return null;
  const hasSubscriptions = r.activeSubscriptions + r.cancelledSubscriptions > 0;
  if (!r.enabled && !hasSubscriptions) return null;

  const rate = r.rateType === "FIXED" ? `${money(r.rate)} on` : `${pct(r.rate)}% of`;
  const capText =
    r.paidCap === null
      ? "for as long as it runs"
      : `for its first ${formatNumber(r.paidCap)} renewal${r.paidCap === 1 ? "" : "s"}`;

  return (
    <DashboardCard id="dashboard-recurring-card" title="Subscriptions" bodyClassName="px-5 pb-5">
      <div className="flex flex-col gap-3">
        {hasSubscriptions ? (
          <>
            <div className="flex items-baseline gap-1 leading-none">
              <span className="text-4xl font-semibold tabular-nums tracking-tight text-foreground">
                {money(r.earnings)}
              </span>
              <span className="text-sm font-medium text-muted-foreground">from renewals</span>
            </div>
            <p className="text-sm leading-relaxed text-foreground">
              {r.renewals > 0 ? (
                <>
                  <strong>{formatNumber(r.renewals)}</strong> renewal{r.renewals === 1 ? "" : "s"} so far across{" "}
                  <strong>{formatNumber(r.activeSubscriptions)}</strong> active subscription
                  {r.activeSubscriptions === 1 ? "" : "s"}
                  {r.cancelledSubscriptions > 0
                    ? `, ${formatNumber(r.cancelledSubscriptions)} cancelled`
                    : ""}
                  {r.lastRenewalAt ? `. Last renewal ${relativeTime(r.lastRenewalAt)}.` : "."}
                </>
              ) : (
                <>
                  <strong>{formatNumber(r.activeSubscriptions)}</strong> active subscription
                  {r.activeSubscriptions === 1 ? "" : "s"} started with your code. The first renewal
                  lands here when it is charged.
                </>
              )}
            </p>
          </>
        ) : (
          <p className="text-sm leading-relaxed text-foreground">
            No subscriptions yet. When a customer subscribes with your code, every renewal keeps
            earning you commission.
          </p>
        )}
        {r.enabled ? (
          <p className="text-xs text-muted-foreground">
            You earn {rate} every renewal of a subscription you brought in, {capText}.
          </p>
        ) : (
          <p className="text-xs text-muted-foreground">
            This program pays on the first order only; renewals are tracked but not commissioned.
          </p>
        )}
      </div>
    </DashboardCard>
  );
}
